import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MRSManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mrsList, setMrsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMRS, setEditingMRS] = useState(null);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [selectedMRS, setSelectedMRS] = useState(null);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [responseFormat, setResponseFormat] = useState('text');
  const [sessionId, setSessionId] = useState('');
  const messagesEndRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    specialty: '',
    disease_category: '',
    study_purpose: '',
    template_id: ''
  });
  
  // Add template-related states
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  
  // Add management-related states
  const [showInactive, setShowInactive] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Add states for new MRS creation workflow
  const [creationStep, setCreationStep] = useState(1); // 1: Choose method, 2: Template/Excel, 3: Details
  const [creationMethod, setCreationMethod] = useState(''); // 'template' or 'excel'
  const [excelFile, setExcelFile] = useState(null);
  const [excelAnalysis, setExcelAnalysis] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadedFileType, setUploadedFileType] = useState(null); // 'excel' | 'pdf'

  const specialties = [
    'Cardiology',
    'Endocrinology',
    'Neurology',
    'Oncology',
    'Psychiatry',
    'Orthopedics',
    'Pediatrics',
    'Geriatrics',
    'Emergency Medicine',
    'Internal Medicine',
    'Other'
  ];

  const studyPurposes = [
    'Clinical Research',
    'Outcomes Analysis',
    'Quality Improvement',
    'Clinical Trials',
    'Epidemiological Study',
    'Registry',
    'Cohort Study',
    'Case Series',
    'Other'
  ];

  const quickAnalysisQuestions = [
    {
      category: "Patient Demographics",
      questions: [
        "What is the age distribution of patients in this dataset?",
        "Show me the gender distribution",
        "What are the demographic characteristics?"
      ]
    },
    {
      category: "Clinical Outcomes",
      questions: [
        "What are the primary outcomes in this study?",
        "Show me treatment success rates",
        "Analyze patient outcomes by therapy type"
      ]
    },
    {
      category: "Safety Analysis",
      questions: [
        "What adverse events were reported?",
        "Show me the safety profile analysis",
        "Identify any concerning safety patterns"
      ]
    },
    {
      category: "Statistical Analysis",
      questions: [
        "Generate a statistical summary of this dataset",
        "Show me correlation analysis between variables",
        "Create a cohort comparison table"
      ]
    }
  ];

  useEffect(() => {
    fetchMRSList();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchMRSList();
    }
  }, [showInactive]);

  useEffect(() => {
    if (showAIAssistant && selectedMRS && !sessionId) {
      setSessionId(`mrs_${selectedMRS.id}_${Date.now()}`);
      // Load existing conversations for this MRS
      loadMRSConversations(selectedMRS.id);
    }
  }, [showAIAssistant, selectedMRS]);

  useEffect(() => {
    scrollToBottom();
  }, [aiMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMRSList = async () => {
    try {
      const response = await axios.get(`${API}/mrs?user_id=${user.id}&include_inactive=${showInactive}`);
      setMrsList(response.data);
    } catch (error) {
      console.error('Error fetching MRS list:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplatesForSpecialty = async (specialty) => {
    if (!specialty) {
      setAvailableTemplates([]);
      return;
    }
    
    setTemplateLoading(true);
    try {
      const response = await axios.get(`${API}/templates/for-mrs?specialty=${specialty}&user_id=${user.id}`);
      setAvailableTemplates(response.data);
      
      // Clear selected template if not compatible with new specialty
      if (selectedTemplate && selectedTemplate.specialty !== specialty) {
        setSelectedTemplate(null);
        setFormData(prev => ({ ...prev, template_id: '' }));
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setAvailableTemplates([]);
    } finally {
      setTemplateLoading(false);
    }
  };

  // New function to fetch ALL templates without specialty filtering
  const fetchAllTemplates = async () => {
    setTemplateLoading(true);
    try {
      const response = await axios.get(`${API}/templates?user_id=${user.id}`);
      // Filter only active templates
      const activeTemplates = response.data.filter(template => template.is_active !== false);
      setAvailableTemplates(activeTemplates);
    } catch (error) {
      console.error('Error fetching all templates:', error);
      setAvailableTemplates([]);
    } finally {
      setTemplateLoading(false);
    }
  };

  const loadMRSConversations = async (mrsId) => {
    try {
      const response = await axios.get(`${API}/mrs/${mrsId}/conversations?user_id=${user.id}`);
      if (response.data.length > 0) {
        // Load the most recent conversation
        const recentConversation = response.data[0];
        setAiMessages(recentConversation.messages || []);
        setSessionId(recentConversation.session_id);
      } else {
        // Start with welcome message
        setAiMessages([{
          role: 'assistant',
          content: `Hello Dr. ${user?.name?.split(' ')[0]}! I'm your AI assistant for the "${selectedMRS?.name}" dataset. I can help you analyze your ${selectedMRS?.patient_count || 0} patients in ${selectedMRS?.specialty}.

**What I can do:**
• **Demographic Analysis** - Age, gender, and population characteristics
• **Clinical Outcomes** - Treatment success rates and patient outcomes  
• **Safety Analysis** - Adverse events and risk pattern identification
• **Statistical Analysis** - Correlations and comparative studies
• **Research Insights** - Publication-ready summaries

**Response Formats:**
• **Text** - Detailed explanations and insights
• **Table** - Structured data for easy comparison
• **Graph** - Data visualization recommendations

Select a response format above and ask me anything about your medical data!`,
          timestamp: new Date().toISOString(),
          format: 'text'
        }]);
      }
    } catch (error) {
      console.error('Error loading MRS conversations:', error);
      // Start with welcome message on error
      setAiMessages([{
        role: 'assistant',
        content: `Welcome to the AI Assistant for "${selectedMRS?.name}"! Ask me anything about your medical data.`,
        timestamp: new Date().toISOString(),
        format: 'text'
      }]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMRS) {
        await axios.put(`${API}/mrs/${editingMRS.id}?user_id=${user.id}`, formData);
      } else {
        await axios.post(`${API}/mrs?user_id=${user.id}`, formData);
      }
      
      setShowCreateModal(false);
      setEditingMRS(null);
      setAvailableTemplates([]);
      setSelectedTemplate(null);
      setFormData({
        name: '',
        description: '',
        specialty: '',
        disease_category: '',
        study_purpose: '',
        template_id: ''
      });
      fetchMRSList();
    } catch (error) {
      console.error('Error saving MRS:', error);
    }
  };

  const handleEdit = (mrs) => {
    setEditingMRS(mrs);
    setFormData({
      name: mrs.name,
      description: mrs.description || '',
      specialty: mrs.specialty,
      disease_category: mrs.disease_category || '',
      study_purpose: mrs.study_purpose || '',
      template_id: mrs.template_id || ''
    });
    
    // Load templates for this specialty
    if (mrs.specialty) {
      fetchTemplatesForSpecialty(mrs.specialty);
    }
    
    setShowCreateModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Fetch templates when specialty changes (still used for editing MRS)
    if (name === 'specialty') {
      fetchTemplatesForSpecialty(value);
    }

    // Handle template selection
    if (name === 'template_id') {
      const template = availableTemplates.find(t => t.id === value);
      setSelectedTemplate(template);
      
      // Automatically set specialty from selected template
      if (template && template.specialty) {
        setFormData(prev => ({
          ...prev,
          specialty: template.specialty
        }));
      }
    }
  };

  const openAIAssistant = (mrs) => {
    // Read deselected IDs saved by FileUpload page from localStorage
    // (localStorage stores deselected IDs; all others are considered selected)
    const saved = localStorage.getItem(`analytics_selected_${mrs.id}`);
    let preDeselectedDatasetIds = [];
    if (saved) {
      try { preDeselectedDatasetIds = JSON.parse(saved); } catch { preDeselectedDatasetIds = []; }
    }
    navigate(`/mrs/${mrs.id}/ai-analysis`, {
      state: { preDeselectedDatasetIds },
    });
  };

  const closeAIAssistant = () => {
    setShowAIAssistant(false);
    setSelectedMRS(null);
    setAiMessages([]);
    setSessionId('');
    setInputMessage('');
  };

  const sendAIMessage = async () => {
    if (!inputMessage.trim() || aiLoading || !selectedMRS) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString(),
      format: 'text'
    };

    setAiMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setAiLoading(true);

    try {
      const response = await axios.post(`${API}/mrs/${selectedMRS.id}/ai-analysis?user_id=${user.id}`, {
        message: inputMessage,
        session_id: sessionId,
        response_format: responseFormat
      });

      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: response.data.timestamp,
        format: response.data.format,
        structured_data: response.data.structured_data,
        mrs_context: response.data.mrs_context
      };

      setAiMessages(prev => [...prev, aiMessage]);
      setSessionId(response.data.session_id);
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString(),
        format: 'text'
      };
      setAiMessages(prev => [...prev, errorMessage]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendAIMessage();
    }
  };

  const formatMessage = (message) => {
    // Enhanced message formatting
    let content = message.content;
    
    // Format bullet points
    content = content.replace(/•/g, '•');
    content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    content = content.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    const lines = content.split('\n').map((line, index) => (
      <div key={index} className="text-left" dangerouslySetInnerHTML={{ __html: line }} />
    ));

    return (
      <div className="message-content text-left">
        {lines}
        {message.structured_data && renderStructuredData(message.structured_data)}
      </div>
    );
  };

  const renderStructuredData = (data) => {
    if (!data) return null;

    if (data.type === 'table' && data.headers && data.data) {
      return (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                {data.headers.map((header, index) => (
                  <th key={index} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.data.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  {data.headers.map((header, cellIndex) => (
                    <td key={cellIndex} className="px-4 py-2 text-sm text-gray-900 border-b">
                      {row[header]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (data.type === 'chart') {
      return (
        <div className="mt-4 p-4 bg-sky-50 border border-sky-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <svg className="h-6 w-6 text-sky-600 mt-1" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
              <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
            </svg>
            <div>
              <h4 className="font-medium text-sky-900">Data Visualization Recommendation</h4>
              <p className="text-sm text-sky-800 mt-1">{data.suggestion}</p>
              {data.data.values && (
                <div className="mt-2 text-xs text-sky-700">
                  <strong>Sample values:</strong> {data.data.values.slice(0, 5).join(', ')}
                  {data.data.values.length > 5 && '...'}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const getSpecialtyColor = (specialty) => {
    const colors = {
      'Cardiology': 'bg-red-100 text-red-800 border-red-200',
      'Endocrinology': 'bg-green-100 text-green-800 border-green-200',
      'Neurology': 'bg-blue-100 text-sky-800 border-sky-200',
      'Oncology': 'bg-purple-100 text-purple-800 border-purple-200',
      'Psychiatry': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'Orthopedics': 'bg-orange-100 text-orange-800 border-orange-200',
      'Pediatrics': 'bg-pink-100 text-pink-800 border-pink-200',
      'Geriatrics': 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return colors[specialty] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleMRSAction = async (mrs, action) => {
    if (action === 'delete') {
      // For delete action, first get deletion info
      try {
        const response = await axios.get(`${API}/mrs/${mrs.id}/deletion-info?user_id=${user.id}`);
        const deletionInfo = response.data;
        
        const deleteMessage = `Are you sure you want to permanently delete this Medical Record Set?

This action will permanently delete:
• Medical Record Set: "${deletionInfo.mrs_name}"
• ${deletionInfo.patient_count} patient record${deletionInfo.patient_count !== 1 ? 's' : ''}
• ${deletionInfo.conversation_count} AI conversation${deletionInfo.conversation_count !== 1 ? 's' : ''}

⚠️ This action cannot be undone. All data will be permanently lost.`;

        setConfirmAction({
          type: action,
          mrs: mrs,
          title: 'Delete Medical Record Set',
          message: deleteMessage,
          confirmText: 'Delete Everything',
          confirmClass: 'bg-red-600 hover:bg-red-700',
          deletionInfo: deletionInfo
        });
      } catch (error) {
        console.error('Error fetching deletion info:', error);
        // Fallback to basic delete confirmation
        setConfirmAction({
          type: action,
          mrs: mrs,
          title: 'Delete Medical Record Set',
          message: 'Are you sure you want to permanently delete this Medical Record Set? This action cannot be undone and will delete all associated patient records.',
          confirmText: 'Delete',
          confirmClass: 'bg-red-600 hover:bg-red-700'
        });
      }
    } else {
      // For other actions, use simple messages
      const actionMessages = {
        deactivate: 'Are you sure you want to deactivate this Medical Record Set? It will be hidden from the main list but data will be preserved.',
        reactivate: 'Are you sure you want to reactivate this Medical Record Set?'
      };

      setConfirmAction({
        type: action,
        mrs: mrs,
        title: `${action.charAt(0).toUpperCase() + action.slice(1)} MRS`,
        message: actionMessages[action],
        confirmText: action.charAt(0).toUpperCase() + action.slice(1),
        confirmClass: 'bg-sky-600 hover:bg-sky-700'
      });
    }
    
    setShowConfirmDialog(true);
  };

  const executeAction = async () => {
    if (!confirmAction) return;

    setActionLoading(true);
    try {
      const { type, mrs } = confirmAction;
      let endpoint;
      let method;

      switch (type) {
        case 'deactivate':
          endpoint = `${API}/mrs/${mrs.id}/deactivate?user_id=${user.id}`;
          method = 'PUT';
          break;
        case 'reactivate':
          endpoint = `${API}/mrs/${mrs.id}/reactivate?user_id=${user.id}`;
          method = 'PUT';
          break;
        case 'delete':
          endpoint = `${API}/mrs/${mrs.id}?user_id=${user.id}`;
          method = 'DELETE';
          break;
        default:
          throw new Error('Invalid action');
      }

      if (method === 'PUT') {
        await axios.put(endpoint);
      } else {
        await axios.delete(endpoint);
      }

      // Refresh the list
      fetchMRSList();
      
      // Show success message
      console.log(`MRS ${type}d successfully`);
      
    } catch (error) {
      console.error(`Error ${confirmAction.type}ing MRS:`, error);
      // You could add a toast notification here
      alert(error.response?.data?.detail || `Failed to ${confirmAction.type} MRS`);
    } finally {
      setActionLoading(false);
      setShowConfirmDialog(false);
      setConfirmAction(null);
    }
  };

  const canManageMRS = (mrs) => {
    return mrs.owner_id === user.id || ['admin', 'hod'].includes(user.role);
  };

  // Functions for new MRS creation workflow
  const handleCreateMRS = () => {
    setShowCreateModal(true);
    setCreationStep(1);
    setCreationMethod('');
    setExcelFile(null);
    setExcelAnalysis(null);
  };

  const handleMethodSelection = (method) => {
    setCreationMethod(method);
    setCreationStep(2);
    
    // If template method is selected, fetch all templates immediately
    if (method === 'template') {
      fetchAllTemplates();
    }
  };

  const handleExcelUpload = async (file) => {
    if (!file) return;

    const isPdf = file.name.toLowerCase().endsWith('.pdf');
    const isExcel = file.name.toLowerCase().match(/\.(xlsx|xls|csv)$/);

    if (!isPdf && !isExcel) {
      alert('Please upload an Excel (.xlsx, .xls), CSV, or PDF file');
      return;
    }

    const maxSize = isPdf ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(isPdf ? 'PDF size must be less than 50MB' : 'File size must be less than 10MB');
      return;
    }

    setExcelFile(file);
    setUploadedFileType(isPdf ? 'pdf' : 'excel');
    // Stay on step 2 so user can complete specialty + disease before proceeding
  };

  const handleCreateFromExcel = async () => {
    if (!excelFile || !formData.name || !formData.specialty || !formData.disease_category) {
      alert('Please fill in all required fields and upload a file');
      return;
    }

    setActionLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', excelFile);
      formDataToSend.append('mrs_name', formData.name);
      formDataToSend.append('mrs_description', formData.description || '');
      formDataToSend.append('study_purpose', formData.study_purpose || '');
      formDataToSend.append('specialty', formData.specialty);
      formDataToSend.append('disease', formData.disease_category);

      if (uploadedFileType === 'pdf') {
        const response = await axios.post(`${API}/templates/create-mrs-from-pdf?user_id=${user.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        alert(response.data.message);
      } else {
        const response = await axios.post(`${API}/templates/create-mrs-from-excel?user_id=${user.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        alert(`Success! Created MRS with ${response.data.patients_imported} patient records imported.`);
      }

      fetchMRSList();
      resetCreateModal();
    } catch (error) {
      console.error('Error creating MRS from file:', error);
      alert('Error creating MRS: ' + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const resetCreateModal = () => {
    setShowCreateModal(false);
    setEditingMRS(null);
    setCreationStep(1);
    setCreationMethod('');
    setExcelFile(null);
    setExcelAnalysis(null);
    setUploadedFileType(null);
    setAvailableTemplates([]);
    setSelectedTemplate(null);
    setFormData({
      name: '',
      description: '',
      specialty: '',
      disease_category: '',
      study_purpose: '',
      template_id: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Medical Record Sets</h1>
          <p className="text-gray-600 mt-2">Manage your clinical datasets with integrated AI analysis</p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {/* Show Inactive Toggle */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="showInactive"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-4 w-4 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
            />
            <label htmlFor="showInactive" className="text-sm text-gray-700">
              Show inactive records
            </label>
          </div>
          
          <button
            onClick={handleCreateMRS}
            className="btn-primary flex items-center space-x-2"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            <span>Create New MRS</span>
          </button>
        </div>
      </div>

      {/* MRS Grid */}
      {mrsList.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mrsList.map((mrs) => (
            <div key={mrs.id} className={`medical-card hover:shadow-lg transition-all duration-200 ${
              mrs.is_active === false ? 'opacity-60 border-gray-300' : ''
            }`}>
              <div className="medical-card-header">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold text-gray-900 truncate">{mrs.name}</h3>
                    {mrs.is_active === false && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Inactive
                      </span>
                    )}
                  </div>
                  
                  {/* Management Dropdown */}
                  {canManageMRS(mrs) && (
                    <div className="relative group">
                      <button className="text-gray-400 hover:text-gray-600 p-1">
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                        </svg>
                      </button>
                      
                      {/* Dropdown Menu */}
                      <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                        <div className="py-1" role="menu">
                          <button
                            onClick={() => handleEdit(mrs)}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                            Edit Details
                          </button>
                          
                          {mrs.is_active !== false ? (
                            <button
                              onClick={() => handleMRSAction(mrs, 'deactivate')}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                              </svg>
                              Deactivate
                            </button>
                          ) : (
                            <button
                              onClick={() => handleMRSAction(mrs, 'reactivate')}
                              className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                            >
                              <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Reactivate
                            </button>
                          )}
                          
                          <div className="border-t border-gray-100 my-1"></div>
                          
                          <button
                            onClick={() => handleMRSAction(mrs, 'delete')}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                          >
                            <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-2">
                  <span className={`specialty-badge border ${getSpecialtyColor(mrs.specialty)}`}>
                    {mrs.specialty}
                  </span>
                </div>
              </div>
              
              <div className="medical-card-content">
                {mrs.description && (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{mrs.description}</p>
                )}
                
                <div className="space-y-2 mb-4">
                  {mrs.disease_category && (
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Disease: {mrs.disease_category}</span>
                    </div>
                  )}
                  
                  {mrs.study_purpose && (
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Purpose: {mrs.study_purpose}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center text-sm text-gray-600">
                    <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                    </svg>
                    <span>{mrs.patient_count} patients</span>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    <span>Created {formatDate(mrs.created_at)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-3">
                  <Link
                    to={`/mrs/${mrs.id}/upload`}
                    className="btn-secondary text-center text-sm py-2"
                  >
                    Upload Data
                  </Link>
                  <Link
                    to={`/mrs/${mrs.id}/patients`}
                    className="btn-secondary text-center text-sm py-2"
                  >
                    View Patients
                  </Link>
                </div>

                {/* AI Assistant Button */}
                <button
                  onClick={() => openAIAssistant(mrs)}
                  className="w-full btn-primary text-sm py-2 flex items-center justify-center space-x-2"
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                  <span>Analyze your data with AI</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <svg className="h-24 w-24 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h3 className="text-xl font-medium text-gray-900 mb-2">No Medical Record Sets</h3>
          <p className="text-gray-600 mb-6">Create your first medical record set to start organizing patient data.</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            Create New MRS
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingMRS ? 'Edit Medical Record Set' : 'Create New Medical Record Set'}
              </h2>
              <button
                onClick={resetCreateModal}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Step 1: Choose Creation Method */}
              {!editingMRS && creationStep === 1 && (
                <div className="space-y-6">
                  <div className="text-center">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Choose Creation Method</h3>
                    <p className="text-sm text-gray-600">How would you like to create your Medical Record Set?</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Option 1: Use Existing Template */}
                    <div 
                      onClick={() => handleMethodSelection('template')}
                      className="border-2 border-gray-200 rounded-lg p-6 cursor-pointer hover:border-sky-500 hover:bg-sky-50 transition-colors"
                    >
                      <div className="text-center">
                        <svg className="h-12 w-12 text-sky-600 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                          <path fillRule="evenodd" d="M3 8a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">Use Existing Template</h4>
                        <p className="text-sm text-gray-600">Choose from your existing form templates to structure data entry</p>
                      </div>
                    </div>
                    
                    {/* Option 2: Upload File (Excel or PDF) */}
                    <div 
                      onClick={() => handleMethodSelection('excel')}
                      className="border-2 border-gray-200 rounded-lg p-6 cursor-pointer hover:border-green-500 hover:bg-green-50 transition-colors"
                    >
                      <div className="text-center">
                        <svg className="h-12 w-12 text-green-600 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">Upload File</h4>
                        <p className="text-sm text-gray-600">Upload an Excel file to create a form template, or a PDF for AI document analysis</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Template Selection or Excel Upload */}
              {!editingMRS && creationStep === 2 && creationMethod === 'template' && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Select Form Template</h3>
                    <p className="text-sm text-gray-600 mb-4">Choose any of your existing form templates to create the Medical Record Set</p>
                  </div>
                  
                  {/* Direct Template Selection - No Specialty Filter */}
                  <div>
                    <label className="form-label">Form Template *</label>
                    {templateLoading ? (
                      <div className="form-input flex items-center justify-center py-3">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sky-600 mr-2"></div>
                        <span className="text-gray-600">Loading all templates...</span>
                      </div>
                    ) : availableTemplates.length === 0 ? (
                      <div className="form-input flex items-center justify-center py-3 bg-gray-50">
                        <div className="text-center">
                          <svg className="h-8 w-8 text-gray-400 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" clipRule="evenodd" />
                            <path fillRule="evenodd" d="M3 8a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                          <p className="text-gray-500 text-sm mb-2">No form templates found</p>
                          <p className="text-xs text-gray-400">Create a form template first to use this option</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <select
                          name="template_id"
                          required
                          className="form-input"
                          value={formData.template_id}
                          onChange={handleChange}
                        >
                          <option value="">Select a form template...</option>
                          {availableTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name} ({template.specialty})
                            </option>
                          ))}
                        </select>
                        
                        {/* Template Preview */}
                        {selectedTemplate && (
                          <div className="mt-2 p-3 bg-sky-50 rounded-lg border border-sky-200">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="text-sm font-medium text-sky-900 mb-1">
                                  Selected Template: {selectedTemplate.name}
                                </h4>
                                <p className="text-xs text-sky-700 mb-2">
                                  Specialty: {selectedTemplate.specialty} • 
                                  Sections: {selectedTemplate.template_data?.sections?.length || 0} •
                                  Created: {new Date(selectedTemplate.created_at).toLocaleDateString()}
                                </p>
                                <div className="text-xs text-sky-600">
                                  <div className="font-medium mb-1">Form sections:</div>
                                  {selectedTemplate.template_data?.sections?.map((section, index) => (
                                    <div key={index} className="inline-block bg-blue-100 text-sky-800 px-2 py-1 rounded mr-1 mb-1">
                                      {section.title} ({section.fields?.length || 0} fields)
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Template Statistics */}
                        {availableTemplates.length > 0 && (
                          <div className="mt-2 text-xs text-gray-500">
                            Found {availableTemplates.length} form template{availableTemplates.length !== 1 ? 's' : ''} across all specialties
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setCreationStep(1)}
                      className="btn-secondary"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setCreationStep(3)}
                      disabled={!formData.template_id}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: File Upload (Excel or PDF) */}
              {!editingMRS && creationStep === 2 && creationMethod === 'excel' && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Upload File</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Upload an Excel (.xlsx, .xls) or CSV file to automatically generate a form template, or upload a PDF to enable AI document analysis.
                    </p>
                  </div>
                  
                  <div>
                    <label className="form-label">Medical Specialty *</label>
                    <select
                      name="specialty"
                      required
                      className="form-input"
                      value={formData.specialty}
                      onChange={handleChange}
                    >
                      <option value="">Select specialty...</option>
                      {specialties.map(specialty => (
                        <option key={specialty} value={specialty}>{specialty}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="form-label">Disease/Condition *</label>
                    <input
                      type="text"
                      name="disease_category"
                      required
                      className="form-input"
                      value={formData.disease_category}
                      onChange={handleChange}
                      placeholder="e.g., Type 2 Diabetes, Hypertension"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">File *</label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                      <div className="space-y-1 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                          <label htmlFor="excel-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-sky-600 hover:text-sky-500">
                            <span>Upload file</span>
                            <input
                              id="excel-upload"
                              name="excel-upload"
                              type="file"
                              accept=".xlsx,.xls,.csv,.pdf"
                              className="sr-only"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) handleExcelUpload(file);
                              }}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">Excel (.xlsx, .xls), CSV up to 10MB &nbsp;·&nbsp; PDF up to 50MB</p>
                      </div>
                    </div>
                    
                    {excelFile && (
                      <div className={`mt-2 p-3 rounded-lg border ${
                        uploadedFileType === 'pdf' ? 'bg-purple-50 border-purple-200' : 'bg-green-50 border-green-200'
                      }`}>
                        <div className="flex items-center">
                          <svg className={`h-5 w-5 mr-2 ${
                            uploadedFileType === 'pdf' ? 'text-purple-600' : 'text-green-600'
                          }`} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className={`text-sm ${
                            uploadedFileType === 'pdf' ? 'text-purple-800' : 'text-green-800'
                          }`}>
                            {uploadedFileType === 'pdf' ? '📄 PDF selected: ' : '📊 Excel selected: '}{excelFile.name}
                          </span>
                        </div>
                        {uploadedFileType === 'pdf' && (
                          <p className="text-xs text-purple-600 mt-1 ml-7">PDF will be indexed for AI analysis. No form template will be created.</p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setCreationStep(1)}
                      className="btn-secondary"
                    >
                      Back
                    </button>
                    {uploadLoading && (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sky-600 mr-2"></div>
                        <span className="text-sm text-gray-600">
                          {uploadedFileType === 'pdf' ? 'Processing PDF...' : 'Analyzing Excel file...'}
                        </span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setCreationStep(3)}
                      disabled={!formData.specialty || !formData.disease_category || !excelFile}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: MRS Details */}
              {(!editingMRS && creationStep === 3) || editingMRS ? (
                <div className="space-y-4">
                  {!editingMRS && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">MRS Details</h3>
                    </div>
                  )}
                  
                  <div>
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="form-input"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g., Diabetes Cohort 2024"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Description</label>
                    <textarea
                      name="description"
                      className="form-input"
                      rows={3}
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Describe the purpose and scope of this medical record set..."
                    />
                  </div>
                  
                  {/* Show selected template info for new MRS creation */}
                  {!editingMRS && selectedTemplate && (
                    <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-sky-900 mb-2">Selected Form Template</h4>
                      <div className="space-y-1 text-sm">
                        <div><span className="font-medium">Template:</span> {selectedTemplate.name}</div>
                        <div><span className="font-medium">Specialty:</span> {selectedTemplate.specialty}</div>
                        <div><span className="font-medium">Sections:</span> {selectedTemplate.template_data?.sections?.length || 0}</div>
                        <div><span className="font-medium">Total Fields:</span> {selectedTemplate.template_data?.sections?.reduce((sum, section) => sum + (section.fields?.length || 0), 0) || 0}</div>
                      </div>
                    </div>
                  )}
                  
                  {editingMRS && (
                    <>
                      <div>
                        <label className="form-label">Medical Specialty *</label>
                        <select
                          name="specialty"
                          required
                          className="form-input"
                          value={formData.specialty}
                          onChange={handleChange}
                        >
                          <option value="">Select specialty...</option>
                          {specialties.map(specialty => (
                            <option key={specialty} value={specialty}>{specialty}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="form-label">Form Template *</label>
                        {templateLoading ? (
                          <div className="form-input flex items-center justify-center py-3">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sky-600 mr-2"></div>
                            <span className="text-gray-600">Loading templates...</span>
                          </div>
                        ) : (
                          <select
                            name="template_id"
                            required
                            className="form-input"
                            value={formData.template_id}
                            onChange={handleChange}
                            disabled={!formData.specialty}
                          >
                            <option value="">
                              {!formData.specialty ? 'Please select a specialty first' : 'Select a form template...'}
                            </option>
                            {availableTemplates.map(template => (
                              <option key={template.id} value={template.id}>
                                {template.name} ({template.specialty})
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </>
                  )}
                  
                  <div>
                    <label className="form-label">Disease Category</label>
                    <input
                      type="text"
                      name="disease_category"
                      className="form-input"
                      value={formData.disease_category}
                      onChange={handleChange}
                      placeholder="e.g., Cardiovascular Disease"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Study Purpose</label>
                    <textarea
                      name="study_purpose"
                      className="form-input"
                      rows={2}
                      value={formData.study_purpose}
                      onChange={handleChange}
                      placeholder="Research objective, clinical trial, etc."
                    />
                  </div>
                  
                  <div className="flex space-x-3 pt-4 border-t border-gray-200">
                    {!editingMRS && (
                      <button
                        type="button"
                        onClick={() => setCreationStep(2)}
                        className="btn-secondary"
                      >
                        Back
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={resetCreateModal}
                      className="flex-1 btn-secondary"
                    >
                      Cancel
                    </button>
                    <button 
                      type={creationMethod === 'excel' ? 'button' : 'submit'}
                      className="flex-1 btn-primary"
                      onClick={creationMethod === 'excel' ? handleCreateFromExcel : undefined}
                      disabled={creationMethod === 'excel' && (actionLoading || !excelFile)}
                    >
                      {actionLoading ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Creating...
                        </div>
                      ) : (
                        editingMRS ? 'Update MRS' : 'Create MRS'
                      )}
                    </button>
                  </div>
                </div>
              ) : null}
            </form>
          </div>
        </div>
      )}

      {/* AI Assistant Modal */}
      {showAIAssistant && selectedMRS && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-4 h-5/6 flex flex-col">
            {/* AI Assistant Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-sky-50 to-indigo-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-10 w-10 bg-sky-600 rounded-full flex items-center justify-center">
                    <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">AI Medical Assistant</h2>
                    <div className="flex items-center space-x-2 text-sm">
                      <span className="text-gray-600">Dataset:</span>
                      <span className="font-medium text-sky-900">{selectedMRS.name}</span>
                      <span className={`specialty-badge text-xs ${getSpecialtyColor(selectedMRS.specialty)}`}>
                        {selectedMRS.specialty}
                      </span>
                      <span className="text-gray-500">• {selectedMRS.patient_count} patients</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  {/* Response Format Selector */}
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">Format:</label>
                    <select
                      value={responseFormat}
                      onChange={(e) => setResponseFormat(e.target.value)}
                      className="text-sm border border-gray-300 rounded px-2 py-1"
                    >
                      <option value="text">Text</option>
                      <option value="table">Table</option>
                      <option value="graph">Graph</option>
                    </select>
                  </div>
                  
                  <button
                    onClick={closeAIAssistant}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {aiMessages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-4xl text-left ${
                      message.role === 'user' 
                        ? 'bg-sky-600 text-white rounded-l-lg rounded-tr-lg' 
                        : 'bg-gray-100 text-gray-800 rounded-r-lg rounded-tl-lg'
                    } px-4 py-3`}
                  >
                    {message.role === 'assistant' && (
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="h-5 w-5 bg-sky-600 rounded-full flex items-center justify-center">
                          <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-xs font-medium text-gray-600">Exlva DARe AI</span>
                        {message.format && message.format !== 'text' && (
                          <span className="text-xs bg-blue-100 text-sky-800 px-2 py-0.5 rounded">
                            {message.format.toUpperCase()}
                          </span>
                        )}
                      </div>
                    )}
                    
                    {formatMessage(message)}
                    
                    <div className={`text-xs mt-2 opacity-70 ${message.role === 'user' ? 'text-sky-100' : 'text-gray-500'}`}>
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
              
              {aiLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-r-lg rounded-tl-lg px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sky-600"></div>
                      <span className="text-gray-600">Analyzing your medical data<span className="loading-dots"></span></span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Questions */}
            {aiMessages.length <= 1 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Analysis Questions:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {quickAnalysisQuestions.map((category, categoryIndex) => (
                    <div key={categoryIndex} className="space-y-2">
                      <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        {category.category}
                      </h4>
                      {category.questions.map((question, questionIndex) => (
                        <button
                          key={questionIndex}
                          onClick={() => setInputMessage(question)}
                          className="w-full text-left text-xs text-sky-600 hover:text-sky-800 hover:bg-sky-50 p-2 rounded transition-colors duration-200"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="px-6 py-4 border-t border-gray-200 bg-white">
              <div className="flex space-x-4">
                <div className="flex-1 relative">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={`Ask me about ${selectedMRS.name} dataset - demographics, outcomes, safety analysis...`}
                    className="form-input resize-none pr-12"
                    rows={2}
                    disabled={aiLoading}
                  />
                  <button
                    onClick={sendAIMessage}
                    disabled={!inputMessage.trim() || aiLoading}
                    className="absolute right-2 bottom-2 p-2 text-sky-600 hover:text-sky-800 disabled:text-gray-400"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <svg className="h-3 w-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>HIPAA Compliant</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <svg className="h-3 w-3 text-sky-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                    </svg>
                    <span>GPT-4 Powered</span>
                  </div>
                </div>
                <span>Press Enter to send • Shift+Enter for new line</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && confirmAction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{confirmAction.title}</h2>
            </div>
            
            <div className="p-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  {confirmAction.type === 'delete' ? (
                    <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="h-6 w-6 text-sky-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                
                <div className="flex-1">
                  <div className="text-sm text-gray-700 whitespace-pre-line">
                    {confirmAction.message}
                  </div>
                  
                  <div className="mt-3 p-3 bg-gray-50 rounded">
                    <p className="text-sm font-medium text-gray-900">MRS: {confirmAction.mrs.name}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      {confirmAction.mrs.specialty} • {confirmAction.mrs.patient_count} patients
                    </p>
                    
                    {confirmAction.type === 'delete' && confirmAction.deletionInfo && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs">
                        <div className="font-medium text-red-900 mb-1">Items to be deleted:</div>
                        <div className="text-red-800 space-y-1">
                          <div>• {confirmAction.deletionInfo.patient_count} patient record{confirmAction.deletionInfo.patient_count !== 1 ? 's' : ''}</div>
                          <div>• {confirmAction.deletionInfo.conversation_count} AI conversation{confirmAction.deletionInfo.conversation_count !== 1 ? 's' : ''}</div>
                          <div className="font-medium mt-1">Total: {confirmAction.deletionInfo.total_items} item{confirmAction.deletionInfo.total_items !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setConfirmAction(null);
                }}
                disabled={actionLoading}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={executeAction}
                disabled={actionLoading}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${confirmAction.confirmClass}`}
              >
                {actionLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  confirmAction.confirmText
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MRSManagement;